// Centralized color helpers for every v2 surface that
// names a deal stage. Two orthogonal signals:
//
//   1. Stage dot color — derived from `stage_type` only
//      (open = copper, won = green, lost = red). Same dot
//      everywhere a stage is named.
//
//   2. Journey tint — background + border of the stage's
//      container (chip / row / column header), derived
//      from the stage's POSITION in the pipeline. Open
//      stages interpolate between a coolest "Lead-end"
//      tone and a warmest "Trial-end" tone. Won and Lost
//      always use their resolved tones regardless of
//      position.
//
// Both signals are exported as raw hex (for `style={...}`
// callsites — SVG strokes, dynamic backgrounds) and, for
// the per-stage_type case, as Tailwind arbitrary-value
// classes (so simple dot/label callsites stay one-liners).

import { msg } from "@lingui/core/macro"
import type { MessageDescriptor } from "@lingui/core"
import type {
  DealStageType,
  DealStatus,
  LinkedDealPipelineStage,
} from "@/types/v2/conversation"

// ──────────────────────────────────────────────────────
// Stage-type palette (dot + label)

export const STAGE_DOT_HEX: Record<DealStageType, string> = {
  open: "#B86A2D",
  won: "#5C8A4F",
  lost: "#B84F4F",
}

const STAGE_DOT_BG_CLASS: Record<DealStageType, string> = {
  open: "bg-[#B86A2D]",
  won: "bg-[#5C8A4F]",
  lost: "bg-[#B84F4F]",
}

const STAGE_LABEL_TEXT_CLASS: Record<DealStageType, string> = {
  open: "text-[#B86A2D]",
  won: "text-[#5C8A4F]",
  lost: "text-[#B84F4F]",
}

export function stageDotColor(stageType: DealStageType): string {
  return STAGE_DOT_BG_CLASS[stageType]
}

// Historical alias — same return as `stageDotColor`. Kept
// so existing callsites that import `stageDotBg` keep
// working; new code can reach for either.
export function stageDotBg(stageType: DealStageType): string {
  return STAGE_DOT_BG_CLASS[stageType]
}

export function stageLabelColor(stageType: DealStageType): string {
  return STAGE_LABEL_TEXT_CLASS[stageType]
}

// Short human label for a stage's outcome type. Used by the
// pipeline-customize editor's type badge ("Open"/"Won"/"Lost")
// and any other surface that names the outcome a stage maps to.
export const STAGE_TYPE_LABEL: Record<DealStageType, MessageDescriptor> = {
  open: msg`Open`,
  won: msg`Won`,
  lost: msg`Lost`,
}

// ──────────────────────────────────────────────────────
// Journey tint (position-based background + border)

export interface StageTint {
  background: string
  border: string
}

// Canonical 5-stop scale for OPEN stages, derived from
// the Paper artboards (NMI-0, MQV-0, N6K-0, NBQ-0, NG7-0).
// The first stop is the coolest "Lead-end" tone; the last
// is the warmest "Trial-end" tone. We interpolate between
// adjacent stops when the tenant pipeline has fewer or
// more than 5 open stages, so the first open stage always
// reads cool and the last open stage before Won always
// reads warm.
const OPEN_STOPS: StageTint[] = [
  { background: "#F4F1EA", border: "#E5DFD3" }, // Lead-end
  { background: "#F6F1E6", border: "#E8DECA" }, // Qualified
  { background: "#F8F0DE", border: "#EBD9BD" }, // Proposal
  { background: "#FAEED2", border: "#EED4A6" }, // Negotiation
  { background: "#FBEAC0", border: "#EFC992" }, // Trial-end
]

const WON_TINT: StageTint = {
  background: "#EAF2E5",
  border: "#C8DCB9",
}

const LOST_TINT: StageTint = {
  background: "#F5E5E3",
  border: "#E5C4BF",
}

// Pick the journey tint for a stage given its position
// within the OPEN stages of the pipeline. `openIndex` is
// 0-based; `openCount` is the total number of open
// stages. Won and Lost ignore both args.
export function stageJourneyTint(
  stageType: DealStageType,
  openIndex: number,
  openCount: number,
): StageTint {
  if (stageType === "won") return WON_TINT
  if (stageType === "lost") return LOST_TINT

  if (openCount <= 1) return OPEN_STOPS[0]

  // Normalize index to [0, 1], then sample the 5-stop
  // ramp. Linear blend between adjacent stops.
  const lastStop = OPEN_STOPS.length - 1
  const t = openIndex / (openCount - 1)
  const scaled = t * lastStop
  const lo = Math.floor(scaled)
  const hi = Math.min(lo + 1, lastStop)
  const frac = scaled - lo

  return {
    background: lerpHex(
      OPEN_STOPS[lo].background,
      OPEN_STOPS[hi].background,
      frac,
    ),
    border: lerpHex(OPEN_STOPS[lo].border, OPEN_STOPS[hi].border, frac),
  }
}

// Walk the pipeline's stages in render order and produce
// a per-stage tint map. Open-stage indices are assigned
// in pipeline order, ignoring won/lost — so a Lost stage
// sitting between two Open stages doesn't shift the
// interpolation curve.
export function buildStageJourneyTints<
  S extends { id: number; stageType: DealStageType },
>(stages: S[]): Map<number, StageTint> {
  const openCount = stages.filter((s) => s.stageType === "open").length
  const tints = new Map<number, StageTint>()
  let openIndex = 0
  for (const stage of stages) {
    if (stage.stageType === "open") {
      tints.set(stage.id, stageJourneyTint("open", openIndex, openCount))
      openIndex += 1
    } else {
      tints.set(stage.id, stageJourneyTint(stage.stageType, 0, 0))
    }
  }
  return tints
}

// ──────────────────────────────────────────────────────
// Pipeline progress bar (past / current / future)

// Filled segment color for the three-state progress strip
// on the deal slideover and the deal detail page. Past
// segments are green; the current segment carries the
// dealStatus-coded copper / green / red; future segments
// are the lightest warm grey.
export function pipelineSegmentColor(
  stage: LinkedDealPipelineStage,
  dealStatus: DealStatus,
): string {
  if (stage.isPassed) {
    return "bg-[#5C8A4F]"
  }
  if (stage.isCurrent) {
    if (dealStatus === "lost") return "bg-[#B84F4F]"
    if (dealStatus === "won") return "bg-[#5C8A4F]"
    return "bg-[#B86A2D]"
  }
  return "bg-[#E5DFD3]"
}

// Label color for the row beneath the progress strip.
// Past stages read in mid-warm grey, the current stage
// uses the stageType accent, future stages read in light
// warm grey. Matches the spec on the deal detail page.
export function pipelineLabelColor(
  stage: LinkedDealPipelineStage,
  dealStatus: DealStatus,
): string {
  if (stage.isCurrent) return stageLabelColor(dealStatusAsStageType(dealStatus))
  if (stage.isPassed) return "text-[#7A746B]"
  return "text-[#9A938A]"
}

function dealStatusAsStageType(status: DealStatus): DealStageType {
  return status
}

// ──────────────────────────────────────────────────────
// Selected-state palette (stage pickers — popover + sheet)

// When a stage option is the currently-selected one, the
// row overlays a dark fill on top of the journey tint.
// Same values across the desktop popover and the mobile
// sheet so the two surfaces read as one component.
export const STAGE_SELECTED_BG = "#1E1B17"
export const STAGE_SELECTED_FG = "#F7F5F0"

// ──────────────────────────────────────────────────────
// Internal: hex interpolation

function lerpHex(a: string, b: string, t: number): string {
  const ar = parseInt(a.slice(1, 3), 16)
  const ag = parseInt(a.slice(3, 5), 16)
  const ab = parseInt(a.slice(5, 7), 16)
  const br = parseInt(b.slice(1, 3), 16)
  const bg = parseInt(b.slice(3, 5), 16)
  const bb = parseInt(b.slice(5, 7), 16)
  const r = Math.round(ar + (br - ar) * t)
  const g = Math.round(ag + (bg - ag) * t)
  const bl = Math.round(ab + (bb - ab) * t)
  return `#${toHex(r)}${toHex(g)}${toHex(bl)}`
}

function toHex(n: number): string {
  return n.toString(16).padStart(2, "0").toUpperCase()
}
